import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, Input, Output, ViewChild, ViewChildren, QueryList, ChangeDetectorRef, EventEmitter, ElementRef, ViewEncapsulation } from '@angular/core';
import { AssistedBrowsingService } from './assisted-browsing.service';
import { AssistedBrowsingFrameComponent } from './assisted-browsing-frame';
import { Subscription } from 'rxjs';
import { SessionState } from './session-state';
import { NavigateAction } from './navigation-bar/navigation-controls/navigate-action';
import { FullScreenHandlerService } from 'shared/services/full-screen-handler/full-screen-handler.service';

@Component({
  selector: 'app-assisted-browsing-panel',
  templateUrl: './assisted-browsing-panel.component.html',
  styleUrls: ['./assisted-browsing-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class AssistedBrowsingPanelComponent implements OnInit, OnDestroy {
  @Input()
  chat: any;

  @Output()
  close = new EventEmitter<any>();

  @ViewChild('assistedBrowsingContainer', { static: true })
  private _assistedBrowsingContainer: ElementRef;

  @ViewChildren('assistedBrowsingFrame')
  private _abFrames: QueryList<AssistedBrowsingFrameComponent>;

  public pageViews = new Map<string, { url: string, subscription: Subscription }>();
  public activePageViewId: string;
  public sessionState: SessionState = SessionState.NONE;
  public visitorTabs: { url: string, title: string, pageViewId: string, selected: boolean }[] = [];
  public activePageUrl: string = '';
  public isHighlightActive: boolean;
  public hasVisitorMonitor: boolean = true;

  private _visitSubscription: Subscription;
  private _closeEventSubscription: Subscription;
  private _boundFullscreenChangeHandler: any;
  private _initialInsertIsDone: boolean;

  constructor(private _service: AssistedBrowsingService,
              private _changeDetectorRef: ChangeDetectorRef) {
    this.isHighlightActive = false;
  }

  ngOnInit(): void {
    this.start();

    this._boundFullscreenChangeHandler = this._onFullscreenChangeHandler.bind(this);
    FullScreenHandlerService.addOnChangeHandler(this._boundFullscreenChangeHandler);
  }

  ngOnDestroy(): void {
    if (this._boundFullscreenChangeHandler) {
      FullScreenHandlerService.removeOnChangeHandler(this._boundFullscreenChangeHandler);
      this._boundFullscreenChangeHandler = null;
    }
  }

  _onFullscreenChangeHandler() {
    // Firefox doesn't detect the fullscreen state change without this
    this._changeDetectorRef.markForCheck();
  }

  start() {
    this._changeSessionState(SessionState.WAITING_FOR_PERMISSION);
    this._service.startSession(this.chat.ChatID, this.chat.VisitID, this.chat.VisitorID)
      .then((session) => {
        this._visitSubscription = session.pageViewObservable.subscribe((event) => this._onVisitChanged(event));
        this._closeEventSubscription = session.closeEvent.subscribe((reason) => this._onCloseSession(reason));
        this._changeSessionState(SessionState.STARTED);
      })
      .catch((reason) => this._onCloseSession(reason));
  }

  /*
   * Close session
   */
  stop() {
    this._changeSessionState(SessionState.CLOSING);
    this._service.stopSession(this.chat.ChatID);
  }

  toggleHighlight() {
    this.isHighlightActive = !this.isHighlightActive;
    this._abFrames.forEach(abf => {
      abf.onMessage({highlight: this.isHighlightActive});
    });
  }

  onNavigationChange($event) {
    switch ($event.action) {
      case NavigateAction.PUSH_PAGE:
        this._service.pushPage(this.chat.ChatID, this.activePageViewId, $event.param);
        break;
      case NavigateAction.BACK:
        this._service.navigateBack(this.chat.ChatID, this.activePageViewId);
        break;
      case NavigateAction.FORWARD:
        this._service.navigateForward(this.chat.ChatID, this.activePageViewId);
        break;
      case NavigateAction.RELOAD:
        this._service.reloadPage(this.chat.ChatID, this.activePageViewId);
        break;
      case NavigateAction.CHANGE_TAB:
        this._changeTab($event.param);
        break;
      default:
        throw Error('Invalid event');
    }
  }

  get isStarted(): boolean {
    return this.sessionState === SessionState.STARTED;
  }

  isFullscreen(): boolean {
    return FullScreenHandlerService.isFullscreen();
  }

  toggleFullscreen(): void {
    if (this.isFullscreen()) {
      FullScreenHandlerService.exitFullscreen();
    } else {
      FullScreenHandlerService.requestFullscreenOnElement(this._assistedBrowsingContainer.nativeElement);
    }
  }

  public isWaitingForPermission() {
    return this.sessionState === SessionState.WAITING_FOR_PERMISSION;
  }

  private _onCloseSession(reason) {
    this._changeSessionState(SessionState.ENDED);
    if (this._visitSubscription) {
      this._visitSubscription.unsubscribe();
      this._visitSubscription = null;
    }
    if (this._closeEventSubscription) {
      this._closeEventSubscription.unsubscribe();
      this._closeEventSubscription = null;
    }
    if (this.pageViews && this.pageViews.size > 0) {
      for (let { subscription } of Array.from(this.pageViews.values())) {
        subscription.unsubscribe();
      }
    }
    this.activePageViewId = null;
    this.close.emit(reason);
  }

  private _onVisitChanged(event) {
    if (event.event && event.event === 'inserted') {
      this._initialInsertIsDone = true;
    }

    if (!event.data) {
      this.decideIfThereIsNoVisitorMonitorSpecified(event);
      return;
    }

    const pageViewId = event.data.PageViewID;
    const url = event.data.Url;

    if (event.data.Ended && this.pageViews.has(pageViewId)) {
      this._processEndedPageView(pageViewId);
      this._changeDetectorRef.markForCheck();
    } else if (!event.data.Ended && !this.pageViews.has(pageViewId)) {
      this._processNewPageView(pageViewId, url);
      this._changeDetectorRef.markForCheck();
    }
  }

  private _processNewPageView(pageViewId: string, url: string) {
    this.pageViews.set(pageViewId, { url, subscription: null });
    this._addPageViewToVisitorTabs(pageViewId, url, url);
    this._markLastPageViewAsActive();
    this._service.syncPageView(this.chat.ChatID, pageViewId)
      .then((conf) => {
        const abf = this._abFrames.find((item: AssistedBrowsingFrameComponent) => item.pageViewId === pageViewId);
        if (abf) {
          abf.setUrl(conf.url);
          const subscription = conf.messageObservable.subscribe((message) => abf.onMessage(message));
          this.pageViews.get(pageViewId).subscription = subscription;
          abf.onMessage({highlight: this.isHighlightActive});
        }
      });
  }

  private _processEndedPageView(pageViewId: string) {
    this.pageViews.delete(pageViewId);
    this._markLastPageViewAsActive();
    this._removePageViewFromVisitorTabs(pageViewId);
  }

  private _addPageViewToVisitorTabs(pageViewId: string, url: string, title: string) {
    this.visitorTabs.unshift({ url, pageViewId, title, selected: false });
  }

  private _removePageViewFromVisitorTabs(pageViewId: string) {
    const index = this.visitorTabs.findIndex((item) => item.pageViewId === pageViewId);
    if (index > -1) {
      this.visitorTabs.splice(index, 1);
    }
  }

  private _markLastPageViewAsActive() {
    const pageViewIds = Array.from(this.pageViews.keys());
    if (pageViewIds.length > 0) {
      const lastEntry = pageViewIds[pageViewIds.length - 1];
      this._changeTab(lastEntry);
    } else {
      this.activePageViewId = null;
      this.activePageUrl = '';
    }
  }

  private decideIfThereIsNoVisitorMonitorSpecified(event) {
    if (!this._initialInsertIsDone && event.event === 'done') {
      this.hasVisitorMonitor = false;
      this._changeDetectorRef.markForCheck();
    }
  }

  private _changeSessionState(newState) {
    this.sessionState = newState;
    this._changeDetectorRef.markForCheck();
  }

  private _changeTab(pageViewId) {
    this.visitorTabs.forEach((item) => item.selected = item.pageViewId === pageViewId);
    const pageView = this.pageViews.get(pageViewId);
    if (pageView) {
      this.activePageViewId = pageViewId;
      this.activePageUrl = pageView.url;
    }
  }
}
